//External Libraries
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faPen as farPen } from '@fortawesome/pro-regular-svg-icons/faPen';
import { faTrashCan as farTrashCan } from '@fortawesome/pro-regular-svg-icons/faTrashCan';
import { faReply as farReply } from '@fortawesome/pro-regular-svg-icons/faReply';

//Utils
import { getTimeAgo, hasCommentBeenEdited } from '../utils';

//Components
import { CMButton } from '@chartmetric/chartmetric-design-system/components/CMButton';
import { CMFlex } from '@chartmetric/chartmetric-design-system/components/CMFlex';
import { CMText } from '@chartmetric/chartmetric-design-system/components/CMText';
import CommentImageThumbnail from './CommentImageThumbnail';
import CommentInput from './CommentInput';
import ImageLightbox from './ImageLightbox';

//Types
import type { AnnouncementCommentEdit, CommentTreeNode } from '../types';

interface Props {
  comment: CommentTreeNode;
  isOwn: boolean;
  isEditing: boolean;
  canSetDisplayName: boolean;
  onEdit: (commentId: number, edit: AnnouncementCommentEdit) => Promise<boolean>;
  onDelete: (commentId: number) => void;
  onReply: (commentId: number, userName: string | null) => void;
  parentUserName?: string;
}

function CommentItem({
  comment,
  isOwn,
  isEditing,
  canSetDisplayName,
  onEdit,
  onDelete,
  onReply,
  parentUserName,
}: Props) {
  const { t } = useTranslation('common', { keyPrefix: 'announcementPanel' });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editValue, setEditValue] = useState(comment.content ?? '');
  const [editDisplayName, setEditDisplayName] = useState(comment.display_name ?? '');
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([]);
  const [newImages, setNewImages] = useState<File[]>([]);
  const [enlargedImage, setEnlargedImage] = useState<{ src: string; alt: string } | null>(null);
  const handleCloseLightbox = useCallback(() => setEnlargedImage(null), []);
  const timeAgo = getTimeAgo(comment.created_at);
  const isEdited = hasCommentBeenEdited(comment);
  const keptAttachments = (comment.attachments ?? []).filter((att) => !removedAttachmentIds.includes(att.id));

  const resetEditState = (): void => {
    setEditValue(comment.content ?? '');
    setEditDisplayName(comment.display_name ?? '');
    setRemovedAttachmentIds([]);
    setNewImages([]);
  };

  const handleStartEdit = (): void => {
    resetEditState();
    setIsEditMode(true);
  };

  const handleCancelEdit = (): void => {
    resetEditState();
    setIsEditMode(false);
  };

  const handleRemoveAttachment = (id: number): void => {
    setRemovedAttachmentIds((prev) => [...prev, id]);
  };

  const handleSaveEdit = async (): Promise<void> => {
    const trimmed = editValue.trim();
    if (!trimmed) return;

    const trimmedDisplayName = editDisplayName.trim();
    const isContentChanged = trimmed !== (comment.content ?? '').trim();
    const isDisplayNameChanged = trimmedDisplayName !== (comment.display_name ?? '').trim();
    if (!isContentChanged && !isDisplayNameChanged && !removedAttachmentIds.length && !newImages.length) {
      handleCancelEdit();
      return;
    }

    const isSaved = await onEdit(comment.id, {
      content: trimmed,
      displayName: isDisplayNameChanged ? trimmedDisplayName : undefined,
      removeAttachmentIds: removedAttachmentIds,
      newImages,
    });
    if (isSaved) handleCancelEdit();
  };

  if (comment.deleted_at !== null) {
    return (
      <CMFlex vertical fullWidth>
        <CMText value={t('comments.deleted')} variant="label-2" color="disabled" />
      </CMFlex>
    );
  }

  return (
    <CMFlex align="center" fullWidth gap="xs">
      <CMFlex vertical fullWidth>
        <CMFlex align="center" gap="sm">
          <CMText value={comment.user_name ?? ''} variant="mini-2" color="secondary" />
          <CMText value={timeAgo} variant="mini-1" color="tertiary" />
          {isEdited && <CMText value={t('comments.edited')} variant="mini-1" color="tertiary" />}
        </CMFlex>
        {isEditMode ? (
          <CommentInput
            inputId={`comment-edit-${comment.id}`}
            value={editValue}
            onChange={setEditValue}
            onSubmit={() => { void handleSaveEdit(); }}
            onCancel={handleCancelEdit}
            submitLabel={t('comments.save')}
            isSubmitting={isEditing}
            canSetDisplayName={canSetDisplayName}
            displayName={editDisplayName}
            onDisplayNameChange={setEditDisplayName}
            existingAttachments={keptAttachments}
            onRemoveExistingAttachment={handleRemoveAttachment}
            pendingImages={newImages}
            onImagesChange={setNewImages}
          />
        ) : (
          <CMFlex vertical gap="xs">
            <CMFlex align="center" gap="xs">
              {parentUserName && <CMText value={'@' + parentUserName} variant="mini-1" color="activePrimary" />}
              <CMText value={comment.content ?? ''} size="sm" />
            </CMFlex>
            {comment.attachments && comment.attachments.length > 0 && (
              <CMFlex gap="xs" wrap>
                {comment.attachments.map((att) => (
                  <CommentImageThumbnail
                    key={att.id}
                    url={att.image_url}
                    alt={att.file_name}
                    onClick={() => setEnlargedImage({ src: att.image_url, alt: att.file_name })}
                  />
                ))}
              </CMFlex>
            )}
          </CMFlex>
        )}
      </CMFlex>
      <CMFlex align="center">
        <CMButton
          iconLeft={farReply}
          variant="secondaryText"
          size="sm"
          onClick={() => onReply(comment.id, comment.user_name)}
        />
        {isOwn && !isEditMode && (
          <CMButton
            iconLeft={farPen}
            variant="secondaryText"
            size="sm"
            onClick={handleStartEdit}
          />
        )}
        {isOwn && !isEditMode && (
          <CMButton
            iconLeft={farTrashCan}
            variant="secondaryText"
            size="sm"
            onClick={() => onDelete(comment.id)}
            _className="hover:text-cm-red-500"
          />
        )}
      </CMFlex>
      {enlargedImage && (
        <ImageLightbox src={enlargedImage.src} alt={enlargedImage.alt} onClose={handleCloseLightbox} />
      )}
    </CMFlex>
  );
}

export default CommentItem;
